/**
 * PKCE (Proof Key for Code Exchange) utilities — RFC 7636.
 *
 * Used for the AppExchange OAuth flow where the frontend builds the Salesforce
 * authorization URL. The code_verifier is generated here, stored in a cookie
 * scoped to the parent domain, and forwarded to the backend alongside the
 * authorization code so the backend can include it in its token exchange POST
 * to Salesforce.
 *
 * WHY COOKIES (not sessionStorage or localStorage):
 * sessionStorage and localStorage are origin-scoped. The install page and the
 * OAuth callback redirect_uri can be on different subdomains:
 *   install page  → app.sms-magic.com
 *   AUS callback  → aus-app.sms-magic.com
 *   EU  callback  → eu.app.sms-magic.com
 * Cookies set with Domain=.sms-magic.com are shared across all *.sms-magic.com
 * subdomains, so the verifier written before the redirect is readable on any
 * regional callback URL. The same logic applies to *.txtbox.in (QA/staging).
 * Cookies are cleared immediately after callback processing (Max-Age=0), so
 * effective persistence is seconds.
 *
 * NOTE — Salesforce Connected App requirement:
 * The Connected App must have "Require Proof Key for Code Exchange (PKCE)"
 * enabled in Salesforce Setup → App Manager → <App> → Edit → OAuth Policies.
 *
 * NOTE — Token refresh:
 * PKCE applies only to the initial authorization code grant.
 * grant_type=refresh_token requests must NOT include code_verifier.
 *
 * NOTE — Logout:
 * Call clearPkceSession() from any logout handler to ensure no stale verifier
 * lingers after the user signs out.
 */

export const PKCE_VERIFIER_KEY = 'sf_pkce_verifier';
export const PKCE_STATE_NONCE_KEY = 'sf_oauth_state_nonce';

/**
 * Generates a cryptographically secure PKCE code verifier (RFC 7636 §4.1).
 *
 * 64 random bytes → 86-character base64url string.
 * Satisfies the 43–128 character length requirement.
 * Characters are drawn from [A-Za-z0-9-_] (a valid subset of [A-Za-z0-9-._~]).
 *
 * Requires a secure context (HTTPS). Throws if crypto.subtle is unavailable —
 * callers must surface this as a visible UI error, never silently swallow it.
 */
export function generateCodeVerifier() {
  if (!window.crypto?.subtle) {
    throw new Error('PKCE requires a secure context (HTTPS). Cannot initiate login.');
  }
  const array = new Uint8Array(64);
  window.crypto.getRandomValues(array);
  return _base64urlEncode(array);
}

/**
 * Derives the PKCE code_challenge from a verifier using SHA-256 (S256 method).
 * Returns a base64url string: + → -, / → _, trailing = stripped.
 *
 * Async because crypto.subtle.digest returns a Promise.
 */
export async function generateCodeChallenge(verifier) {
  if (!window.crypto?.subtle) {
    throw new Error('PKCE requires a secure context (HTTPS). Cannot initiate login.');
  }
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(verifier));
  return _base64urlEncode(new Uint8Array(digest));
}

/**
 * Generates a random 32-hex-character nonce for embedding in the OAuth state
 * parameter. Provides CSRF protection — a fresh nonce is generated per login
 * attempt and validated when Salesforce redirects back.
 */
export function generateStateNonce() {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Persists verifier and state nonce in parent-domain cookies before the OAuth
 * redirect. Overwrites any previous values — safe to call at the start of each
 * login attempt. Max-Age=300 auto-expires cookies after 5 minutes as a safety net.
 */
export function storePkceSession(verifier, nonce) {
  _setCookie(PKCE_VERIFIER_KEY, encodeURIComponent(verifier), 300);
  _setCookie(PKCE_STATE_NONCE_KEY, encodeURIComponent(nonce), 300);
}

export function retrievePkceVerifier() {
  const val = _getCookie(PKCE_VERIFIER_KEY);
  return val ? decodeURIComponent(val) : null;
}

export function retrieveStateNonce() {
  const val = _getCookie(PKCE_STATE_NONCE_KEY);
  return val ? decodeURIComponent(val) : null;
}

/**
 * Clears both PKCE cookies immediately (Max-Age=0).
 * Must be called after the callback is processed (success or failure) and on logout.
 */
export function clearPkceSession() {
  _setCookie(PKCE_VERIFIER_KEY, '', 0);
  _setCookie(PKCE_STATE_NONCE_KEY, '', 0);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns the parent domain to scope cookies across all subdomains.
 *   app.sms-magic.com     → .sms-magic.com
 *   aus-app.sms-magic.com → .sms-magic.com
 *   eu.app.sms-magic.com  → .sms-magic.com
 *   qa-app.txtbox.in      → .txtbox.in
 * Returns empty string for localhost (no domain restriction needed).
 */
function _parentDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  const parts = hostname.split('.');
  return '.' + parts.slice(-2).join('.');
}

function _setCookie(name, value, maxAge) {
  const domain = _parentDomain();
  const isLocal = !domain;
  const parts = [
    `${name}=${value}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (!isLocal) parts.push('Secure');
  const cookieStr = parts.join('; ');
  console.log('[PKCE] _setCookie:', {
    name,
    domain: domain || '(none — localhost)',
    maxAge,
    hostname: window.location.hostname,
    cookieStr,
  });
  document.cookie = cookieStr;
}

function _getCookie(name) {
  console.log('[PKCE] _getCookie: looking for', name);
  console.log('[PKCE] _getCookie: document.cookie =', document.cookie || '(empty)');
  console.log('[PKCE] _getCookie: current hostname =', window.location.hostname);
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const eqIdx = cookie.indexOf('=');
    if (eqIdx === -1) continue;
    const key = cookie.slice(0, eqIdx).trim();
    const val = cookie.slice(eqIdx + 1).trim();
    if (key === name) {
      console.log('[PKCE] _getCookie: found', name, '=', decodeURIComponent(val));
      return val;
    }
  }
  console.log('[PKCE] _getCookie: not found for', name);
  return null;
}

// base64url encoding: standard base64 with + → -, / → _, no trailing =
function _base64urlEncode(buffer) {
  let str = '';
  for (let i = 0; i < buffer.length; i++) {
    str += String.fromCharCode(buffer[i]);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
